import { usePaginatedQuery, useUrlPaginatedQuery } from "@shared/hooks";
import type { PaginatedQueryParams } from "@shared/api";
import { axiosGetData } from "@shared/api/clients";
import { useQuery } from "@tanstack/react-query";

import type {
  LicenseDetails,
  LicenseFilters,
  LicenseListItem,
  ModuleItem,
} from "./licenses.types";

const LICENSES_QUERY_KEY = "licenses";
const MODULES_QUERY_KEY = "modules";
const getModulesEndpoint = (tenantId: string) =>
  `/v1/managerial/tenants/${tenantId}/modules`;
const getLicensesEndpoint = (tenantId: string) =>
  `v1/managerial/tenants/${tenantId}/licenses`;

export const LICENSE_FILTER_KEYS: (keyof LicenseFilters)[] = [
  "structureName",
  "complexName",
  "moduleName",
  "sortBy",
  "sortOrder",
];

export const getLicenseById = async (
  tenantId: string,
  licenseId: string,
): Promise<LicenseDetails> => {
  return axiosGetData<LicenseDetails>(
    `${getLicensesEndpoint(tenantId)}/${licenseId}`,
  );
};

export const useGetLicenseByIdQuery = (
  tenantId: string,
  licenseId: string,
  options = {},
) => {
  return useQuery({
    queryKey: [LICENSES_QUERY_KEY, tenantId, licenseId],
    queryFn: () => getLicenseById(tenantId, licenseId),
    enabled: !!tenantId && !!licenseId,
    ...options,
  });
};

export const useListLicensesQuery = (
  tenantId: string,
  params?: PaginatedQueryParams<LicenseFilters>,
) => {
  return usePaginatedQuery<LicenseListItem, LicenseFilters>({
    endpoint: getLicensesEndpoint(tenantId),
    queryKeyBase: [LICENSES_QUERY_KEY, tenantId],
    params,
    enabled: !!tenantId,
  });
};

export const useListLicensesUrlQuery = (tenantId: string) => {
  return useUrlPaginatedQuery<LicenseListItem, LicenseFilters>({
    endpoint: getLicensesEndpoint(tenantId),
    queryKeyBase: [LICENSES_QUERY_KEY, tenantId],
    filterKeys: LICENSE_FILTER_KEYS,
    enabled: !!tenantId,
  });
};

export const getModules = async (tenantId: string): Promise<ModuleItem[]> => {
  return axiosGetData<ModuleItem[]>(getModulesEndpoint(tenantId));
};

export const useListModulesQuery = (tenantId: string) => {
  return useQuery({
    queryKey: [MODULES_QUERY_KEY, tenantId],
    queryFn: () => getModules(tenantId),
    enabled: !!tenantId ,
  });
};
